import request from '../request';

/**
 * Get library line items (where tripdayid is null)
 * @returns {Promise<Object>} - The API response.
 */
export const getLibraryLineItems = async () =>
  await request.get('/lineitems/library/all');

/**
 * Get line items for a specific trip day
 * @param {number} tripdayid - The trip day ID.
 * @returns {Promise<Object>} - The API response.
 */
export const getLineItemsByTripDay = async (tripdayid) =>
  await request.get(`/tripdays/${tripdayid}/lineitems`);

/**
 * Get a specific line item by ID
 * @param {number} lineitemid - The line item ID.
 * @returns {Promise<Object>} - The API response.
 */
export const getLineItem = async (lineitemid) =>
  await request.get(`/lineitems/${lineitemid}`);

/**
 * Create a new line item
 * @param {Object} payload - The line item data.
 * @returns {Promise<Object>} - The API response.
 */
export const createLineItem = async (payload) =>
  await request.post('/lineitems', payload);

/**
 * Update an existing line item
 * @param {number} lineitemid - The line item ID.
 * @param {Object} payload - The updated line item data.
 * @returns {Promise<Object>} - The API response.
 */
export const updateLineItem = async (lineitemid, payload) =>
  await request.put(`/lineitems/${lineitemid}`, payload);

/**
 * Delete a line item (soft delete)
 * @param {number} lineitemid - The line item ID.
 * @returns {Promise<Object>} - The API response.
 */
export const deleteLineItem = async (lineitemid) =>
  await request.delete(`/lineitems/${lineitemid}`);

/**
 * Update the sequence of line items
 * @param {Object} payload - Object containing sequences array.
 * @returns {Promise<Object>} - The API response.
 */
export const updateLineItemsSequence = async (payload) =>
  await request.put('/lineitems/sequence', payload);

/**
 * Copy a library line item to a trip day
 * @param {Object} payload - Object containing librarylineitemid, tripdayid, tripid, seq.
 * @returns {Promise<Object>} - The API response.
 */
export const copyLineItemFromLibrary = async (payload) =>
  await request.post('/lineitems/copy-from-library', payload);

/**
 * Add an image to a line item
 * @param {number} lineitemid - The line item ID.
 * @param {Object} payload - Object containing resourcepath, imagename, seq.
 * @returns {Promise<Object>} - The API response.
 */
export const addLineItemImage = async (lineitemid, payload) =>
  await request.post(`/lineitems/${lineitemid}/images`, payload);

/**
 * Update a line item image
 * @param {number} lineitemimageid - The line item image ID.
 * @param {Object} payload - Object containing resourcepath, imagename, seq.
 * @returns {Promise<Object>} - The API response.
 */
export const updateLineItemImage = async (lineitemimageid, payload) =>
  await request.put(`/lineitems/images/${lineitemimageid}`, payload);

/**
 * Delete a line item image
 * @param {number} lineitemimageid - The line item image ID.
 * @returns {Promise<Object>} - The API response.
 */
export const deleteLineItemImage = async (lineitemimageid) =>
  await request.delete(`/lineitems/images/${lineitemimageid}`);

/**
 * Add an attachment to a line item
 * @param {number} lineitemid - The line item ID.
 * @param {Object} payload - Object containing resourcepath, attachmentname, seq.
 * @returns {Promise<Object>} - The API response.
 */
export const addLineItemAttachment = async (lineitemid, payload) =>
  await request.post(`/lineitems/${lineitemid}/attachments`, payload);

/**
 * Delete a line item attachment
 * @param {number} lineitemattachmentid - The line item attachment ID.
 * @returns {Promise<Object>} - The API response.
 */
export const deleteLineItemAttachment = async (lineitemattachmentid) =>
  await request.delete(`/lineitems/attachments/${lineitemattachmentid}`);

/**
 * Copy a trip line item to library
 * @param {number} lineitemid - The line item ID to copy to library.
 * @returns {Promise<Object>} - The API response.
 */
export const copyLineItemToLibrary = async (lineitemid) =>
  await request.post(`/lineitems/${lineitemid}/copy-to-library`);

/**
 * Upload an image file to a line item (simplified - handles S3 upload + DB record creation)
 * @param {number} lineitemid - The line item ID.
 * @param {File} file - The image file to upload.
 * @param {number} [seq] - The sequence number for ordering (optional).
 * @returns {Promise<Object>} - The API response with created LineItemImage record.
 */
export const uploadLineItemImage = async (lineitemid, file, seq) =>
  await request.post(`/lineitems/${lineitemid}/images/upload`, file, {
    headers: {
      'Content-Type': file.type || 'image/jpeg',
      'X-File-Name': file.name,
      'X-Sequence': seq !== undefined ? String(seq) : undefined,
      'Cache-Control': 'no-cache'
    }
  });

/**
 * Upload an attachment file to a line item (simplified - handles S3 upload + DB record creation)
 * @param {number} lineitemid - The line item ID.
 * @param {File} file - The attachment file to upload.
 * @param {number} [seq] - The sequence number for ordering (optional).
 * @returns {Promise<Object>} - The API response with created LineItemAttachment record.
 */
export const uploadLineItemAttachment = async (lineitemid, file, seq) =>
  await request.post(`/lineitems/${lineitemid}/attachments/upload`, file, {
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-File-Name': file.name,
      'X-Sequence': seq !== undefined ? String(seq) : undefined,
      'Cache-Control': 'no-cache'
    }
  });

/**
 * Delete a line item image with S3 cleanup (simplified - handles both DB and S3 deletion)
 * @param {number} lineitemid - The line item ID.
 * @param {number} lineitemimageid - The line item image ID.
 * @returns {Promise<Object>} - The API response.
 */
export const deleteLineItemImageWithFiles = async (
  lineitemid,
  lineitemimageid
) => await request.delete(`/lineitems/${lineitemid}/images/${lineitemimageid}`);

/**
 * Delete a line item attachment with S3 cleanup (simplified - handles both DB and S3 deletion)
 * @param {number} lineitemid - The line item ID.
 * @param {number} lineitemattachmentid - The line item attachment ID.
 * @returns {Promise<Object>} - The API response.
 */
export const deleteLineItemAttachmentWithFiles = async (
  lineitemid,
  lineitemattachmentid
) =>
  await request.delete(
    `/lineitems/${lineitemid}/attachments/${lineitemattachmentid}`
  );
